import React, { useEffect, useState } from 'react';
import {
  FiDollarSign,
  FiFileText,
  FiRepeat,
  FiUsers,
  FiTrendingUp,
  FiTrendingDown,
  FiClock
} from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import { authFetch, profileAPI, getRecentActivity } from '../utils/api';
import { ensureValidTenantId } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import AddTransactionModal from '../components/Modals/AddTransactionModal.js';
import SimpleTrialBanner from '../components/subscription/SimpleTrialBanner';
import WelcomeSplash from '../components/WelcomeSplash';

const paymentColors = {
  successful: '#3498db',
  pending: '#f39c12',
  failed: '#e91e63',
  completed: '#27ae60'
};

const DashboardAnalytics = () => {
  const { t } = useTranslation();
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [invoiceTrend, setInvoiceTrend] = useState([]);
  const [paymentsBreakdown, setPaymentsBreakdown] = useState([]);
  const [customersSummary, setCustomersSummary] = useState({ total: 0, newThisMonth: 0, topCustomers: [] });
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      authFetch('/dashboard/revenue-trend')
        .then(r => (r.ok ? r.json() : [])),
      authFetch('/dashboard/invoice-trend')
        .then(r => (r.ok ? r.json() : [])),
      authFetch('/dashboard/payments-breakdown')
        .then(r => (r.ok ? r.json() : [])),
      authFetch('/dashboard/customers-summary')
        .then(r => (r.ok ? r.json() : { total: 0, newThisMonth: 0, topCustomers: [] })),
    ])
      .then(([revenue, invoices, payments, customers]) => {
        // Format dates for better display
        const formattedRevenue = revenue.map(item => ({
          ...item,
          date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        }));

        const formattedInvoices = invoices.map(item => ({
          ...item,
          date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          count: parseInt(item.count) || 0
        }));

        // Ensure payments breakdown has proper structure
        const formattedPayments = payments.map(item => ({
          ...item,
          count: parseInt(item.count) || 0
        }));

        setRevenueTrend(formattedRevenue);
        setInvoiceTrend(formattedInvoices);
        setPaymentsBreakdown(formattedPayments);
        setCustomersSummary(customers);
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}>Loading analytics...</div>;

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: 24, 
        marginBottom: 24 
      }}>
        {/* Revenue Trend */}
        <div style={{ 
          background: 'linear-gradient(145deg, #ffffff, #f8f9fa)', 
          borderRadius: 20, 
          padding: 24, 
          boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
          border: '1px solid rgba(255,255,255,0.8)'
        }}>
          <h3 style={{ color: '#2c3e50', marginBottom: 20, fontSize: 18, fontWeight: 700 }}>{t('dashboard.revenueTrend', 'Revenue Trend')}</h3>
          <ResponsiveContainer width="100%" height={240}>
            {revenueTrend.length > 0 ? (
              <BarChart data={revenueTrend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#888', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#888', fontSize: 12}} dx={-10} />
                <Tooltip 
                  contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                  formatter={(value) => [`₹${Number(value).toLocaleString()}`, 'Revenue']} 
                />
                <Bar dataKey="revenue" fill="url(#colorRevenue)" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={1}/>
                    <stop offset="95%" stopColor="#60a5fa" stopOpacity={0.8}/>
                  </linearGradient>
                </defs>
              </BarChart>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', background: '#f8fafc', borderRadius: 12 }}>
                {t('dashboard.notEnoughData', 'Not enough data yet')}
              </div>
            )}
          </ResponsiveContainer>
        </div>

        {/* Invoice Trend */}
        <div style={{ 
          background: 'linear-gradient(145deg, #ffffff, #f8f9fa)', 
          borderRadius: 20, 
          padding: 24, 
          boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
          border: '1px solid rgba(255,255,255,0.8)'
        }}>
          <h3 style={{ color: '#2c3e50', marginBottom: 20, fontSize: 18, fontWeight: 700 }}>{t('dashboard.invoiceActivity', 'Invoice Activity')}</h3>
          <ResponsiveContainer width="100%" height={240}>
            {invoiceTrend.length > 0 ? (
              <LineChart data={invoiceTrend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#888', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#888', fontSize: 12}} dx={-10} />
                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                <Line type="monotone" dataKey="count" stroke="#e91e63" strokeWidth={4} dot={{ r: 4, fill: '#e91e63', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 8 }} />
              </LineChart>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', background: '#f8fafc', borderRadius: 12 }}>
                {t('dashboard.noInvoices', 'No invoices generated')}
              </div>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: 24 
      }}>
        {/* Payments Breakdown */}
        <div style={{ 
          background: 'linear-gradient(145deg, #ffffff, #f8f9fa)', 
          borderRadius: 20, 
          padding: 24, 
          boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
          border: '1px solid rgba(255,255,255,0.8)'
        }}>
          <h3 style={{ color: '#2c3e50', marginBottom: 20, fontSize: 18, fontWeight: 700 }}>{t('dashboard.paymentStatus', 'Payment Status')}</h3>
          <ResponsiveContainer width="100%" height={240}>
            {paymentsBreakdown.length > 0 ? (
              <PieChart>
                <Pie
                  data={paymentsBreakdown}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                >
                  {paymentsBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={paymentColors[entry.status] || '#8884d8'} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                <Legend verticalAlign="bottom" height={36} iconType="circle" />
              </PieChart>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', background: '#f8fafc', borderRadius: 12 }}>
                {t('dashboard.noPayments', 'No payments recorded')}
              </div>
            )}
          </ResponsiveContainer>
        </div>

        {/* Top Customers */}
        <div style={{ 
          background: 'linear-gradient(145deg, #ffffff, #f8f9fa)', 
          borderRadius: 20, 
          padding: 24, 
          boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
          border: '1px solid rgba(255,255,255,0.8)'
        }}>
          <h3 style={{ color: '#2c3e50', marginBottom: 20, fontSize: 18, fontWeight: 700 }}>{t('dashboard.topCustomers', 'Top Customers')}</h3>
          {customersSummary.topCustomers && customersSummary.topCustomers.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {customersSummary.topCustomers.map((c, i) => (
                <li key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  marginBottom: 12,
                  background: '#f8fafc',
                  borderRadius: 12,
                  transition: 'transform 0.2s',
                  cursor: 'default'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
                      {c.name.charAt(0).toUpperCase()}
                    </div>
                    <span style={{ color: '#334155', fontWeight: 600 }}>{c.name}</span>
                  </div>
                  <span style={{ color: '#059669', fontWeight: 700 }}>₹{Number(c.totalSpent).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '180px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12 }}>
              {t('dashboard.noCustomers', 'No customers found')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { t } = useTranslation();
  ensureValidTenantId();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchStats() {
      const res = await authFetch('/dashboard/stats');
      if (res.ok) {
        setStats(await res.json());
      }
    }
    fetchStats();
    async function fetchProfile() {
      const data = await profileAPI.getProfile();
      setProfile(data);
    }
    fetchProfile();
    async function fetchRecent() {
      try {
        const data = await authFetch('/dashboard/recent-activity').then(res => res.ok ? res.json() : []);
        setRecentActivity(data);
      } catch (e) {
        setRecentActivity([]);
      }
    }
    fetchRecent();
  }, []);

  if (!stats || !profile) return <div>Loading...</div>;
  const statCards = [
    {
      title: t('dashboard.totalRevenue', 'Total Revenue'),
      value: `₹${Number(stats.totalRevenue).toLocaleString()}`,
      icon: <FaRupeeSign />,
      color: 'var(--primary-color)'
    },
    {
      title: t('dashboard.totalInvoices', 'Total Invoices'),
      value: stats.totalInvoices,
      icon: <FiFileText />,
      color: '#4CAF50'
    },
    {
      title: t('dashboard.totalTransactions', 'Total Transactions'),
      value: stats.totalTransactions,
      icon: <FiRepeat />,
      color: '#FF9800'
    },
    {
      title: t('dashboard.activeCustomers', 'Active Customers'),
      value: stats.activeCustomers,
      icon: <FiUsers />,
      color: 'var(--primary-color)'
    }
  ];

  const handleAddTransaction = async (transactionData) => {
    // You may want to POST this to your backend or handle as needed
    setAddModalOpen(false);
    // Optionally, show a toast or refresh dashboard data
  };

  return (
    <div className="page-container">
      <WelcomeSplash />
      {/* Simple Trial Banner - Shows for ALL users */}
      <SimpleTrialBanner />

      <div className="page-header">
        <div>
          {profile.name ? (
            <h1>Welcome back, {profile.name}!</h1>
          ) : (
            <h1>
              <span style={{ color: '#e91e63', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/settings')}
              >
                Set your username
              </span>
              &nbsp;to personalize your dashboard!
            </h1>
          )}
          <p className="text-muted">Here's what's happening with your business today.</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-primary" onClick={() => navigate('/invoices')}>+ New Invoice</button>
          <button className="btn btn-outline-primary" onClick={() => setAddModalOpen(true)}>+ NEW TRANSACTION</button>
        </div>
      </div>

      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 24,
        marginBottom: 32
      }}>
        {statCards.map((stat, index) => (
          <div key={index} style={{
            background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
            borderRadius: 20,
            padding: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 20,
            boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
            border: '1px solid rgba(255,255,255,0.8)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'default'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.08)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.04)'; }}
          >
            <div style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `${stat.color}15`, // 15 is hex for ~8% opacity
              color: stat.color,
              fontSize: 24
            }}>
              {stat.icon}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: '#64748b', fontSize: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stat.title}</span>
              <span style={{ color: '#0f172a', fontSize: 24, fontWeight: 800 }}>{stat.value}</span>
            </div>
          </div>
        ))}
      </div>

      <DashboardAnalytics />

      <div style={{ 
        background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
        borderRadius: 20,
        padding: 32,
        boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
        border: '1px solid rgba(255,255,255,0.8)',
        marginTop: 16
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>{t('dashboard.recentActivity', 'Recent Activity')}</h2>
          <button style={{ 
            background: '#f1f5f9', color: '#3b82f6', border: 'none', padding: '8px 16px', 
            borderRadius: 8, fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.background = '#e2e8f0'}
          onMouseOut={(e) => e.currentTarget.style.background = '#f1f5f9'}
          >
            {t('dashboard.viewAll', 'View All')}
          </button>
        </div>

        <div>
          {recentActivity.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0', color: '#94a3b8', background: '#f8fafc', borderRadius: 12 }}>
              {t('dashboard.noRecentActivity', 'No recent activity found.')}
            </div>
          ) : (
            recentActivity.map((activity, index) => (
              <div key={index} style={{
                display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0',
                borderBottom: index < recentActivity.length - 1 ? '1px solid #f1f5f9' : 'none'
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: activity.type === 'invoice' ? '#fce7f3' : activity.type === 'transaction' ? '#dcfce7' : '#e0e7ff',
                  color: activity.type === 'invoice' ? '#db2777' : activity.type === 'transaction' ? '#16a34a' : '#4f46e5',
                  fontSize: 20
                }}>
                  {activity.type === 'invoice' && <FiFileText />}
                  {activity.type === 'transaction' && <FaRupeeSign />}
                  {activity.type === 'customer' && <FiUsers />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#334155', marginBottom: 4 }}>{activity.title}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b' }}>
                    <FiClock size={12} />
                    {new Date(activity.date).toLocaleString()}
                  </div>
                </div>
                {activity.amount && (
                  <div style={{ fontSize: 16, fontWeight: 700, color: activity.type === 'invoice' ? '#db2777' : '#16a34a' }}>
                    ₹{Number(activity.amount).toLocaleString()}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <AddTransactionModal
        show={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddTransaction}
      />
    </div>
  );
};

export default DashboardPage; 